import type { FloatingSide } from '@/lib/floating';
import { FLOATING_GAP, FLOATING_MARGIN } from '@/lib/floating';
import { useFloatingPlacement } from '@/lib/use-floating-placement';
import { cn } from '@/lib/utils';
import type {
  ComponentPropsWithRef,
  ReactElement,
  FocusEvent as ReactFocusEvent,
  ReactNode,
  PointerEvent as ReactPointerEvent,
  Ref,
} from 'react';
import { Children, cloneElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * L'infobulle de l'application : une bulle DESSINÉE, blanche, jamais rognée
 * (issue #103 ; `Tooltip.html` du design system PMS, variante Simple).
 *
 * ## Elle renverse la règle que ce dépôt portait
 *
 * Jusqu'ici, `version-history.tsx` et sept autres en-têtes disaient : « le
 * tooltip est le `title` natif et jamais une bulle dessinée, parce qu'une bulle
 * sombre dessinée serait la seule surface sombre d'une application claire ».
 * Le raisonnement tenait, sa PRÉMISSE était fausse : le design system dessine
 * la bulle en BLANC - « le blanc est le fond unique de la marque PMS, jamais le
 * noir, même pour un composant transitoire ». Une bulle blanche à texte gris
 * 900 ne pose aucune surface sombre, et le `title` natif, lui, est dessiné par
 * le navigateur : sur un poste en thème sombre il sort blanc sur noir, et aucun
 * CSS de ce dépôt ne peut le rattraper. C'est le défaut qui a ouvert le lot.
 *
 * ## Elle CLONE son enfant plutôt que de l'envelopper
 *
 * Une enveloppe change la mise en page : un `span` de plus au milieu d'un
 * `flex` prend une ligne de grille, hérite d'un `gap`, casse un `ml-auto`.
 * Trente sites d'appel, c'est trente régressions possibles pour un élément qui
 * ne dessine rien. Alors l'enfant unique est cloné : il reçoit les gestionnaires
 * de survol et de focus, la référence qui sert à le mesurer, et
 * `aria-describedby` quand il le faut. Le seul contrat imposé à l'appelant est
 * d'être UN élément qui accepte un `ref` et des gestionnaires - ce que sont les
 * boutons, les liens et les `span` de tous les sites migrés.
 *
 * ## `aria-describedby` seulement quand la bulle dit AUTRE CHOSE que le nom
 *
 * Le design system demande les deux sur un bouton-icône, et il a raison sur le
 * fond : le nom doit exister sans la bulle, parce qu'au tactile il n'y a pas de
 * bulle. Mais poser une description IDENTIQUE au nom fait lire le même mot deux
 * fois à une synthèse vocale - « Expériences, Expériences ». Donc le nom reste
 * la responsabilité de l'appelant (`aria-label` ou texte visible), et la
 * description n'est posée que lorsque son texte en diffère.
 *
 * ## Rien au tap
 *
 * « Pas de tooltip au tap. Le contenu doit exister ailleurs. » Un pointeur
 * tactile est ignoré : sur un téléphone la bulle n'a pas de survol où vivre, et
 * une bulle ouverte au premier tap mangerait le second.
 */

export interface TooltipProps {
  /** Ce que la bulle dit. Une bulle sans texte ne s'ouvre pas. */
  readonly label: ReactNode;
  /** Le côté préféré ; la bulle bascule d'elle-même si la fenêtre l'en empêche. */
  readonly side?: FloatingSide;
  /**
   * La largeur maximale, quand le défaut du design system ne suffit pas.
   *
   * 240 px est taillé pour un LIBELLÉ - « Se déconnecter », « Passer en
   * production » - et c'est ce que portent les vingt-huit sites génériques.
   * Les panneaux de l'issue 104 portent autre chose : une liste d'étapes, une
   * rédaction de plusieurs phrases. Ils le disent ici plutôt que de poser une
   * classe que le style en ligne de la bulle ne laisserait de toute façon pas
   * gagner - ce qu'un lot a découvert en essayant.
   */
  readonly maxWidth?: number;
  /**
   * Faux quand le déclencheur est DÉJÀ décrit par le même texte.
   *
   * Le composant retire de lui-même la description qui répéterait le NOM
   * accessible, mais il ne voit pas une description qui existe déjà : le tag de
   * ton et le point du slider visent chacun une copie `sr-only` de leur propre
   * phrase, qui reste parce qu'elle est la réponse au clavier et au tactile.
   * Sans ce faux, une synthèse vocale lirait la phrase deux fois de suite.
   */
  readonly describes?: boolean;
  /** Élément unique, qui accepte un `ref` et des gestionnaires de pointeur. */
  readonly children: ReactElement;
  readonly 'data-testid'?: string;
}

/** Design system : 300 ms est le seuil de l'intention, en deçà la bulle clignote. */
export const TOOLTIP_SHOW_DELAY_MS = 300;
/** Le focus clavier est explicite par nature : aucune attente. */
export const TOOLTIP_FOCUS_DELAY_MS = 0;
/** La grâce avant fermeture, qui évite le clignotement entre deux cibles voisines. */
export const TOOLTIP_HIDE_DELAY_MS = 100;
/** La sortie en fondu seul, sans échelle : instable à cette taille. */
const EXIT_MS = 80;

/** Le défaut du design system, taillé pour un libellé. */
const MAX_WIDTH = 240;

/** Les props que le clone reçoit - toutes optionnelles côté appelant. */
type ClonedProps = {
  ref?: Ref<HTMLElement>;
  'aria-label'?: string;
  'aria-describedby'?: string;
  onPointerEnter?: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerLeave?: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerDown?: (event: ReactPointerEvent<HTMLElement>) => void;
  onFocus?: (event: ReactFocusEvent<HTMLElement>) => void;
  onBlur?: (event: ReactFocusEvent<HTMLElement>) => void;
};

/** Appelle celui de l'appelant, puis le nôtre : jamais l'inverse, jamais à sa place. */
function chain<E>(theirs: ((event: E) => void) | undefined, ours: (event: E) => void) {
  return (event: E) => {
    theirs?.(event);
    ours(event);
  };
}

/**
 * La bulle elle-même, montée dans un portail et placée à la mesure.
 *
 * Un composant à part, et pas un bloc de JSX : elle n'existe que pendant qu'elle
 * est ouverte, donc son état d'entrée se rejoue à chaque ouverture sans qu'on
 * ait à le remettre à zéro. Le `useLayoutEffect` du placement s'accroche à un
 * nœud qui vient de naître, ce qui est aussi ce qui fait que la première mesure
 * tombe avant la première peinture.
 */
function Bubble({
  id,
  anchor,
  label,
  side,
  leaving,
  maxWidth,
  testId,
  onEnter,
  onLeave,
}: {
  readonly id: string;
  readonly anchor: HTMLElement;
  readonly label: ReactNode;
  readonly side: FloatingSide;
  readonly leaving: boolean;
  readonly maxWidth: number;
  readonly testId: string | undefined;
  readonly onEnter: () => void;
  readonly onLeave: () => void;
}) {
  /*
    Le placement est le partagé (issue 104) : quatre copies de cet effet
    vivaient dans le paquet, un défaut trouvé sur une capture n'a été corrigé
    que dans l'une d'elles, et c'est ce que `use-floating-placement.ts` existe
    pour empêcher. La bulle est la seule des quatre dont la LARGEUR se mesure
    au lieu d'être fixée : elle suit son texte jusqu'à son plafond.
  */
  const { at, panelRef } = useFloatingPlacement({ anchor, width: null, side });
  const [shown, setShown] = useState(false);

  // L'entrée se joue à la frame suivante : montée déjà à l'état final, il n'y
  // aurait aucune transition à parcourir.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const vertical = at.side === 'top' || at.side === 'bottom';
  const origin = vertical
    ? `${at.arrow}px ${at.side === 'top' ? '100%' : '0'}`
    : `${at.side === 'left' ? '100%' : '0'} ${at.arrow}px`;

  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role="tooltip"
      data-side={at.side}
      data-testid={testId}
      onPointerEnter={onEnter}
      onPointerLeave={onLeave}
      style={{
        left: at.left,
        top: at.top,
        maxWidth,
        /*
          La hauteur, bornée par la fenêtre, et le défilement qui va avec.

          `placeFloating` serre le coin HAUT du panneau, pas sa hauteur : une
          bulle plus haute que la fenêtre atterrit à 8 px du bord et pend en
          dessous. Un libellé ne s'en approche jamais ; un ton de 2000
          caractères, le plafond du schéma, fait neuf cents pixels et perdait
          sa fin sans le dire. Elle défile donc, ce que seule une bulle
          SURVOLABLE peut se permettre - et celle-ci l'est, `onEnter` la
          retenant pendant qu'on la lit.
        */
        maxHeight: `calc(100vh - ${FLOATING_MARGIN * 2}px)`,
        transformOrigin: origin,
      }}
      className={`fixed z-50 w-max rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-caption text-gray-900 leading-relaxed overflow-y-auto shadow-md transition-[opacity,transform] duration-[120ms] ease-out motion-reduce:transition-opacity ${
        shown && !leaving ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}
    >
      {label}
      <Arrow side={at.side} at={at.arrow} />
    </div>,
    document.body,
  );
}

/**
 * Le triangle, dessiné en DEUX : un gris qui porte la bordure, un blanc par
 * dessus qui porte le fond.
 *
 * Un seul triangle ne peut pas avoir de bordure sur deux arêtes et rien sur la
 * troisième - une bordure CSS sur un élément de taille nulle EST le triangle.
 * Les deux superposés, décalés d'un pixel, donnent l'arête grise du design
 * system sans que le panneau ait à la dessiner.
 */
function triangle(side: FloatingSide, at: number, depth: number, flank: number, color: string) {
  const base = { position: 'absolute', width: 0, height: 0 } as const;
  const across = `${flank}px solid transparent`;
  const point = `${depth}px solid ${color}`;
  // Le décalage d'un pixel vers l'intérieur : c'est lui qui laisse dépasser
  // l'arête grise dessinée juste en dessous.
  const out = depth === FLOATING_GAP - 1 ? '100%' : 'calc(100% - 1px)';
  switch (side) {
    case 'top':
      return {
        ...base,
        top: out,
        left: at - flank,
        borderLeft: across,
        borderRight: across,
        borderTop: point,
      };
    case 'bottom':
      return {
        ...base,
        bottom: out,
        left: at - flank,
        borderLeft: across,
        borderRight: across,
        borderBottom: point,
      };
    case 'left':
      return {
        ...base,
        left: out,
        top: at - flank,
        borderTop: across,
        borderBottom: across,
        borderLeft: point,
      };
    case 'right':
      return {
        ...base,
        right: out,
        top: at - flank,
        borderTop: across,
        borderBottom: across,
        borderRight: point,
      };
  }
}

function Arrow({ side, at }: { readonly side: FloatingSide; readonly at: number }) {
  return (
    <>
      <span
        aria-hidden="true"
        style={triangle(side, at, FLOATING_GAP - 1, 5, 'var(--color-gray-200)')}
      />
      <span
        aria-hidden="true"
        style={triangle(side, at, FLOATING_GAP - 2, 4, 'var(--color-blanc)')}
      />
    </>
  );
}

export function Tooltip({
  label,
  side = 'top',
  maxWidth = MAX_WIDTH,
  describes: mayDescribe = true,
  children,
  'data-testid': testId,
}: TooltipProps) {
  const id = useId();
  /*
    `Children.only` rend un `ReactElement<unknown>` : le cast dit ce que la prop
    promet déjà - un élément qui accepte un `ref` et des gestionnaires - et il
    est la seule façon de le dire sans imposer aux trente appelants un type
    d'enfant qui refuserait `<button>` autant que `<span>`.
  */
  const child = Children.only(children) as ReactElement<ClonedProps>;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const show = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hide = useRef<ReturnType<typeof setTimeout> | null>(null);
  /*
    Le dernier geste venait-il d'un POINTEUR.

    Un navigateur envoie `pointerdown`, puis `focus`, puis `click` : la
    fermeture au `pointerdown` était donc défaite par l'ouverture au focus
    0 ms qui la suivait, et presser un bouton faisait clignoter sa bulle puis
    la réinstallait par-dessus la barre qu'on venait d'actionner. Vérifié dans
    un vrai navigateur ; `fireEvent.pointerDown` n'a pas cet effet de bord,
    c'est pourquoi le test qui affirmait l'inverse passait.

    Un `ref` et pas un état : il est lu et écrit dans le même tour de
    gestionnaires, et un rendu de plus ne servirait rien.
  */
  const pressed = useRef(false);

  /** Une bulle vide ne s'ouvre pas, donc elle ne décrit rien non plus. */
  const speaks = label !== '' && label !== null && label !== undefined;

  const clearTimers = useCallback(() => {
    if (show.current !== null) clearTimeout(show.current);
    if (hide.current !== null) clearTimeout(hide.current);
    show.current = null;
    hide.current = null;
  }, []);

  const close = useCallback(() => {
    clearTimers();
    setLeaving(false);
    setOpen(false);
  }, [clearTimers]);

  /**
   * La grâce, puis le fondu, puis le démontage.
   *
   * `TOOLTIP_HIDE_DELAY_MS` est l'attente PLEINE - la bulle reste entière -
   * pendant laquelle le pointeur peut traverser les 8 px qui la séparent de son
   * déclencheur sans la perdre. C'est ce que le design system appelle la grâce,
   * et c'est ce qui rend une bulle de deux lignes lisible. Le fondu ne commence
   * qu'après, et dure `EXIT_MS`.
   */
  const dismiss = useCallback(() => {
    /*
      Le désarmement vient AVANT la sortie anticipée, et c'est tout le défaut
      qu'un test a trouvé : un pointeur qui traverse le déclencheur et repart
      avant le seuil laissait son minuteur d'ouverture courir, et la bulle
      s'ouvrait 300 ms plus tard sur un pointeur parti ailleurs.
    */
    clearTimers();
    if (!open) return;
    hide.current = setTimeout(() => {
      setLeaving(true);
      hide.current = setTimeout(() => {
        hide.current = null;
        setLeaving(false);
        setOpen(false);
      }, EXIT_MS);
    }, TOOLTIP_HIDE_DELAY_MS);
  }, [clearTimers, open]);

  /**
   * Retenir une bulle qui partait.
   *
   * Le pointeur est entré dans la bulle pendant sa grâce ou son fondu : on
   * désarme, ET on remet `leaving` à faux. Sans cette seconde moitié - le
   * défaut qu'une revue a trouvé - la bulle restait montée à `opacity-0`
   * indéfiniment, invisible mais présente, son `aria-describedby` toujours posé
   * et son écouteur d'Échap toujours accroché.
   */
  const keep = useCallback(() => {
    clearTimers();
    setLeaving(false);
  }, [clearTimers]);

  const arm = useCallback(
    (delay: number) => {
      clearTimers();
      setLeaving(false);
      if (delay === 0) {
        setOpen(true);
        return;
      }
      show.current = setTimeout(() => {
        show.current = null;
        setOpen(true);
      }, delay);
    },
    [clearTimers],
  );

  /** Une bulle ouverte sur un composant qu'on démonte laisserait tourner ses minuteurs. */
  useEffect(() => clearTimers, [clearTimers]);

  /*
    Échap ferme, et c'est un écouteur de DOCUMENT monté seulement pendant qu'une
    bulle est ouverte : trente déclencheurs qui écouteraient en permanence
    seraient trente écouteurs pour une bulle que personne n'a ouverte.
  */
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  /*
    Une ancre retirée du document - une ligne qui disparaît sous la bulle -
    répond des zéros à `getBoundingClientRect`, et la bulle sauterait dans le
    coin de la fenêtre. La leçon est celle de `use-variable-fiche.ts` (#88).
  */
  useEffect(() => {
    if (open && anchor !== null && !anchor.isConnected) close();
  });

  const props = child.props as ClonedProps;
  /*
    Voir l'en-tête : une description identique au nom se lit deux fois. La
    comparaison porte sur le nom EXPLICITE de l'appelant, et un déclencheur qui
    peint son libellé en clair sans `aria-label` y échappe encore - le seul cas
    du dépôt est le « + » du bandeau de pages, noté là-bas.
  */
  const describes =
    mayDescribe && speaks && !(typeof label === 'string' && props['aria-label'] === label);

  const clone = cloneElement<ClonedProps>(child, {
    ref: (node: HTMLElement | null) => {
      setAnchor(node);
      const theirs = props.ref;
      if (typeof theirs === 'function') theirs(node);
      else if (theirs !== null && theirs !== undefined && typeof theirs === 'object') {
        (theirs as { current: HTMLElement | null }).current = node;
      }
    },
    ...(open && describes
      ? { 'aria-describedby': [props['aria-describedby'], id].filter(Boolean).join(' ') }
      : {}),
    onPointerEnter: chain(props.onPointerEnter, (event) => {
      if (event.pointerType === 'touch') return;
      pressed.current = false;
      arm(TOOLTIP_SHOW_DELAY_MS);
    }),
    onPointerLeave: chain(props.onPointerLeave, (event) => {
      if (event.pointerType === 'touch') return;
      pressed.current = false;
      dismiss();
    }),
    /*
      Un clic est un geste, pas une question : la bulle s'efface plutôt que de
      rester en travers de ce qu'elle vient d'ouvrir. `pressed` porte la
      décision jusqu'au `focus` qui suit immédiatement, sans quoi celui-ci la
      rouvrirait dans le même souffle.
    */
    onPointerDown: chain(props.onPointerDown, () => {
      pressed.current = true;
      close();
    }),
    onFocus: chain(props.onFocus, () => {
      if (pressed.current) return;
      arm(TOOLTIP_FOCUS_DELAY_MS);
    }),
    onBlur: chain(props.onBlur, () => {
      pressed.current = false;
      close();
    }),
  });

  return (
    <>
      {clone}
      {open && anchor !== null && speaks ? (
        <Bubble
          id={id}
          anchor={anchor}
          label={label}
          side={side}
          leaving={leaving}
          maxWidth={maxWidth}
          testId={testId}
          onEnter={keep}
          onLeave={dismiss}
        />
      ) : null}
    </>
  );
}

/**
 * L'enveloppe d'un contrôle DÉSACTIVÉ, pour que sa bulle puisse s'ouvrir.
 *
 * Un contrôle `disabled` ne reçoit ni pointeur ni focus : le navigateur les
 * supprime, et ils ne remontent pas jusqu'à lui. Une bulle accrochée au contrôle
 * lui-même ne s'ouvrirait donc jamais. Cela ne coûte rien là où la bulle répète
 * le nom du bouton, qui reste lisible ; cela coûte tout là où sa phrase n'existe
 * QUE pendant que le contrôle est fermé, parce qu'elle devient injoignable
 * exactement quand elle sert.
 *
 * **Il y a SEPT contrôles dans ce cas** - la croix d'une tranche de notation et
 * les six outils de la barre de texte riche - et une première rédaction n'en
 * avait vu qu'un, ce qu'une revue a rattrapé. D'où ce composant plutôt que sept
 * copies d'un `span`.
 *
 * Le design system nomme le cas et sa réponse : une enveloppe focalisable porte
 * la description, le contrôle reste désactivé à l'intérieur. Focalisable
 * SEULEMENT quand le contrôle est fermé : ouvert, il est lui-même dans l'ordre
 * de tabulation, et deux arrêts pour un bouton en feraient un de trop.
 */
export function TooltipShield({
  closed,
  className,
  children,
  ...rest
}: Omit<ComponentPropsWithRef<'span'>, 'className' | 'children' | 'tabIndex'> & {
  /** Vrai quand le contrôle à l'intérieur est `disabled`. */
  readonly closed: boolean;
  /** Ce que l'enveloppe reprend de l'élément qu'elle remplace dans la mise en page. */
  readonly className?: string;
  readonly children: ReactNode;
}) {
  /*
    `...rest` n'est PAS de la politesse : `Tooltip` clone son enfant unique pour
    lui poser ses gestionnaires et sa référence, et cet enfant est cette
    enveloppe. Sans l'étalement, elle les avalait tous - la bulle ne s'ouvrait
    plus du tout sur les sept contrôles concernés, et c'est un test qui l'a dit.
  */
  return (
    <span
      {...rest}
      tabIndex={closed ? 0 : undefined}
      className={cn(
        'inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      {children}
    </span>
  );
}
